import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  uploadFichierObligataire,
  getFichierObligataireList,
} from "../helper/Api/AdminApi";

const FichierObligataire = () => {
  const [files, setFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    file: null,
    fileName: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 25, 50, 'All'];
  const [searchTerm, setSearchTerm] = useState('');

  const baseURL = "https://api.amwaly.ma/uploads";

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const json = await getFichierObligataireList();

      if (json?.status === "success") {
        setFiles(
          json.data.FichierObligataire.map((item) => ({
            id: item.ID,
            FileName: item.FileName,
            CreatedOn: item.CreatedOn,
            CreatedBy: item.CreatedBy,
            SystemGeneratedFileName: item.SystemGeneratedFileName,
            IsFromFichierActionBackup: item.IsFromFichierActionBackup,
          }))
        );
      }
    } catch (err) {
      toast.error("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = files.filter(file => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      (file.CreatedBy?.toLowerCase() || '').includes(searchLower) ||
      (file.FileName?.toLowerCase() || '').includes(searchLower)
    );
  });

  const totalItems = filteredFiles.length;
  const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));

  const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
  const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;

  const paginatedFiles = pageSize === 'All'
    ? filteredFiles
    : filteredFiles.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let start = currentPage - halfVisible;
      let end = currentPage + halfVisible;

      if (start < 1) {
        start = 1;
        end = maxVisiblePages;
      }

      if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxVisiblePages + 1;
      }

      for (let i = start; i <= end; i++) pages.push(i);
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (pageSize === 'All') {
      setCurrentPage(1);
    }
  }, [pageSize]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.file) {
      newErrors.file = 'Please upload an Excel file';
    } else if (
      !formData.file.name.endsWith('.xlsx') &&
      !formData.file.name.endsWith('.xls')
    ) {
      newErrors.file = 'Only Excel files (.xlsx or .xls) are allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, file, fileName: file ? file.name : '' });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await uploadFichierObligataire(formData.file);

      if (response?.status === "success") {
        toast.success("File uploaded successfully!");
        setShowModal(false);

        fetchFiles();

        // Reset form
        setFormData({ file: null, fileName: '' });
        setErrors({});
      } else {
        toast.error(response?.message || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (fileId) => {
    toast.info(`Download initiated for file ID: ${fileId}`);
    window.open(`${baseURL}/${fileId}`);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* Show dropdown */}
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm text-secondary-light">Show:</span>
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={loading}
              >
                {pageSize === 'All' ? 'All' : pageSize}
              </button>
              <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                {pageSizeOptions.map((option) => (
                  <li key={option}>
                    <button
                      className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                      onClick={() => handlePageSizeChange(option)}
                      disabled={loading}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Search input */}
          <form className="navbar-search">
            <input
              type="text"
              className="form-control radius-8 h-40-px w-auto"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
        </div>

        <button
          className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          onClick={() => {
            setFormData({ file: null, fileName: '' });
            setShowModal(true);
          }}
          disabled={loading}
        >
          <Icon icon="ic:baseline-plus" className="icon text-lg line-height-1" />
          Add New
        </button>
      </div>

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading Fichier Obligataire...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th className="text-center">Action</th>
                    <th>File Name</th>
                    <th>Uploaded By</th>
                    <th>Uploaded On</th>
                    <th>File Status</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedFiles.length > 0 ? (
                    paginatedFiles.map((file) => (
                      <tr key={file.id}>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleDownload(file.SystemGeneratedFileName)}
                              disabled={loading}
                            >
                              <Icon icon="lucide:download" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                        <td>{file.FileName}</td>
                        <td>{file.CreatedBy}</td>
                        <td>{file.CreatedOn ? new Date(file.CreatedOn).toLocaleString() : 'N/A'}</td>
                        <td>
                          {file.IsFromFichierActionBackup === 1 ? "Backup" : "Current"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-20">
                        {files.length === 0 ? (
                          <div>
                            <p>No fichier obligataire records found</p>
                            <button
                              className="btn btn-sm btn-primary mt-2"
                              onClick={() => {
                                setFormData({ file: null, fileName: '' });
                                setShowModal(true);
                              }}
                            >
                              Upload your first file
                            </button>
                          </div>
                        ) : (
                          "No files match your search"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
              <div className="d-flex align-items-center gap-3">
                <span className="text-secondary-light text-sm">
                  Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                  {Math.min(endIndex, totalItems)} of {totalItems} entries
                </span>
              </div>

              {pageSize !== 'All' && totalPages > 1 && (
                <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-left" className="text-lg" />
                    </button>
                  </li>

                  {getPageNumbers().map((page) => (
                    <li className="page-item" key={page}>
                      <button
                        className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${currentPage === page
                            ? "bg-primary-600 text-white shadow-sm"
                            : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                          }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-right" className="text-lg" />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {/* Upload Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border" style={{ borderRadius: '12px' }}>
              <div className="modal-header p-24 border-bottom">
                <h5 className="modal-title text-md text-primary-light">
                  Add New File
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>

              <div className="modal-body p-24">
                <div className="row mb-20">
                  <div className="col-md-12">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Upload Excel File <span className="text-danger-600">*</span>
                    </label>

                    <input
                      type="file"
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      disabled={loading}
                    />

                    {errors.file && (
                      <span className="text-danger-600 text-sm mt-1">{errors.file}</span>
                    )}

                    {formData.file && (
                      <div className="mt-2 text-sm text-success">
                        Selected file: {formData.fileName}
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Uploading..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FichierObligataire;